import { Icons } from '@/utils/Icons';
import { Link } from 'react-router-dom';
import UserDropdown from './UserDropdown';
import { useEffect, useRef, useState } from 'react';
import SetButtonValueModal from './SetButtonValueModal';
import RulesModal from '@/components/RulesModal';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  setAgeModalOpen,
  setDepositModalOpen,
  setRulesModalOpen,
  setWithdrawModalOpen,
  openAuthModal,
} from '@/store/slices/uiSlice';
import DepositModal from '@/components/deposit/DepositModal';
import WithdrawModal from '@/components/withdraw/WithdrawModal';
import AgeRestrictionModal from '@/components/AgeRestrictionModal';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import {
  useGetContactSupportQuery,
  useGetUserInfoQuery,
  useGetUserWalletsQuery,
} from '@/store/api/userApi';
import SearchDropdown from '@/components/common/SearchDropdown';
import LiveMatchTicker from '@/components/LiveMatchTicker';
import { useIsDemoUser, useIsSelfRegistered } from '@/hooks/redux';
import { isGuestUser } from '@/utils';
import ExposureSummaryModal from './ExposureSummaryModal';
import BrandLogo from '@/components/common/BrandLogo';
import ThemeToggle from '@/components/common/ThemeToggle';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { useTranslation } from '@/i18n';

/** Opens the withdraw popup. On phones (`compact`) only the icon shows, to fit the top row. */
const WithdrawButton = ({ compact = false }: { compact?: boolean }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => dispatch(setWithdrawModalOpen(true))}
      className={`inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md border border-shell-border bg-shell-raised text-xs font-bold uppercase text-shell-fg transition hover:border-gold ${
        compact ? 'w-8 sm:w-auto sm:px-3' : 'px-3'
      }`}
    >
      <Icons.withdraw className="h-4 w-4" aria-hidden="true" />
      <span className={compact ? 'max-sm:sr-only' : ''}>{t('common.withdraw')}</span>
    </button>
  );
};

/** Opens the deposit popup. On phones (`compact`) only the icon shows, to fit the top row. */
const DepositButton = ({ compact = false }: { compact?: boolean }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => dispatch(setDepositModalOpen(true))}
      className={`inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md bg-gold text-xs font-bold uppercase text-gold-foreground transition hover:bg-gold-hover ${
        compact ? 'w-8 sm:w-auto sm:px-3' : 'px-3'
      }`}
    >
      <Icons.deposit className="h-4 w-4" aria-hidden="true" />
      <span className={compact ? 'max-sm:sr-only' : ''}>{t('common.deposit')}</span>
    </button>
  );
};

const AuthButtons = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => dispatch(openAuthModal('login'))}
        className="h-9 rounded-md border border-shell-border px-4 text-sm font-semibold text-shell-fg transition hover:border-gold"
      >
        {t('auth.login')}
      </button>
      <button
        type="button"
        onClick={() => dispatch(openAuthModal('signup'))}
        className="h-9 rounded-md bg-gold px-4 text-sm font-semibold text-gold-foreground transition hover:bg-gold-hover"
      >
        {t('auth.signup')}
      </button>
    </div>
  );
};

type BalanceChipProps = {
  balance: string;
  exposure: string;
  bonus: string;
  showBalance?: boolean;
  showExposure?: boolean;
  onExposureClick: () => void;
};

const BalanceChip = ({
  balance,
  exposure,
  bonus,
  showBalance = true,
  showExposure = true,
  onExposureClick,
}: BalanceChipProps) =>
  // Both hidden from the user menu: drop the whole chip, not just its text (it left an empty
  // bordered box behind).
  !showBalance && !showExposure ? null : (
    <div className="flex items-center gap-2 rounded-md border border-shell-border bg-shell-raised px-2 py-1 leading-tight sm:px-3">
      <Icons.wallet className="hidden shrink-0 text-lg text-gold sm:block" />
      <div className="text-right sm:text-left">
        {showBalance && (
          <div className="text-sm font-bold text-shell-fg">
            <span className="mr-1 font-normal text-shell-muted">Bal</span>
            {balance}
          </div>
        )}
        {showExposure && (
          <div className="flex items-center gap-2 text-[11px] text-shell-muted">
            <button type="button" onClick={onExposureClick} className="hover:text-shell-fg">
              Exp <b className="text-danger-red">{exposure}</b>
            </button>
            {/* Dropped on the narrowest phones so the header row fits. */}
            <span className="max-[379px]:hidden">
              Bonus <b className="text-shell-fg">{bonus}</b>
            </span>
          </div>
        )}
      </div>
    </div>
  );

/**
 * Publishes the header's rendered height as `--header-h` on <html>, so the sticky sport strip
 * can sit right under it. The phone header's height varies (the live-match row comes and goes).
 */
const usePublishHeaderHeight = () => {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const publish = () =>
      document.documentElement.style.setProperty('--header-h', `${element.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const Header = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const headerRef = usePublishHeaderHeight();
  const [showBalance, setShowBalance] = useState(true);
  const [showExposure, setShowExposure] = useState(true);
  const [isExposureModalOpen, setIsExposureModalOpen] = useState(false);
  const isAuthenticated = !isGuestUser();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { ageModalOpen } = useAppSelector((state) => state.ui);
  const isSelfRegistered = useIsSelfRegistered();
  useGetUserInfoQuery(undefined, { pollingInterval: 5000, skip: !isAuthenticated });
  useGetUserWalletsQuery(undefined, { pollingInterval: 5000, skip: !isAuthenticated });
  useGetContactSupportQuery(undefined);
  const { info, walletInfo } = useAppSelector((state) => state.user);
  const username = info?.username ?? 'Demo';
  const isDemoUser = useIsDemoUser();
  const mainWallet = walletInfo?.find((item) => item?.type?.toLowerCase() == 'main');
  const mainBalance = Number(
    Number(mainWallet?.amount ?? 0) -
      Math.abs(Number(mainWallet?.exposureAmount ?? 0)) -
      Number(mainWallet?.lockedAmount ?? 0)
  ).toFixed(2);
  const bonusWallet = walletInfo?.find((wallet) => wallet.type?.toLowerCase() === 'bonus');

  const exposure = Number(mainWallet?.exposureAmount ?? 0).toFixed(2);
  const bonusBalance = Number(bonusWallet?.amount ?? 0).toFixed(2);
  const showWalletActions = isAuthenticated && isSelfRegistered && !isDemoUser;

  const userDropdown = (
    <UserDropdown
      isDemoUser={isDemoUser}
      showBalance={showBalance}
      showExposure={showExposure}
      handleBalanceChange={setShowBalance}
      handleExposureChange={setShowExposure}
      username={username}
    />
  );

  return (
    // Sticky on every screen size, so the header stays in view while scrolling.
    <header
      ref={headerRef}
      className="sticky top-0 z-40 border-b border-shell-border bg-shell-panel shadow-lg"
    >
      {/* Desktop */}
      <div className="hidden h-[72px] items-center gap-6 px-4 xl:flex">
        <Link to="/home" className="shrink-0">
          <BrandLogo className="h-12 w-auto object-contain" />
        </Link>

        <div className="ml-auto flex min-w-0 items-center gap-3 [&>*]:shrink-0">
          {/* Live matches, right before the search box; the only part that shrinks. */}
          <LiveMatchTicker className="!shrink" />
          <div className="w-[260px] 2xl:w-[340px]">
            <SearchDropdown />
          </div>
          <button
            type="button"
            onClick={() => dispatch(setRulesModalOpen(true))}
            title={t('common.rules')}
            className="flex size-9 items-center justify-center rounded-md border border-shell-border text-shell-muted transition hover:border-gold hover:text-shell-fg"
          >
            <Icons.Rules />
          </button>
          <ThemeToggle />
          <LanguageSwitcher />
          {!isAuthenticated ? (
            <AuthButtons />
          ) : (
            <>
              <BalanceChip
                balance={mainBalance}
                exposure={exposure}
                bonus={bonusBalance}
                onExposureClick={() => setIsExposureModalOpen(true)}
              />
              {showWalletActions && <DepositButton />}
              {showWalletActions && <WithdrawButton />}
              {userDropdown}
            </>
          )}
        </div>
      </div>

      {/* Mobile / tablet */}
      <div className="xl:hidden">
        <div className="flex h-14 items-center gap-2 px-3">
          <button
            type="button"
            onClick={onMenuClick}
            aria-label="Open menu"
            className="flex size-9 shrink-0 items-center justify-center rounded-md bg-shell-raised text-shell-fg"
          >
            <Icons.Menu />
          </button>
          <Link to="/home" className="shrink-0">
            <BrandLogo className="h-7 w-auto object-contain max-[379px]:h-6 sm:h-9" />
          </Link>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            {/* On phones the toggle lives in the sidebar drawer to keep this row from overflowing */}
            <ThemeToggle className="max-sm:hidden" />
            <LanguageSwitcher className="max-sm:hidden" />
            {!isAuthenticated ? (
              <AuthButtons />
            ) : (
              <>
                {/* Same order as desktop: balance, Deposit, Withdraw, user menu. */}
                <BalanceChip
                  balance={mainBalance}
                  exposure={exposure}
                  bonus={bonusBalance}
                  showBalance={showBalance}
                  showExposure={showExposure}
                  onExposureClick={() => setIsExposureModalOpen(true)}
                />
                {showWalletActions && <DepositButton compact />}
                {showWalletActions && <WithdrawButton compact />}
                {userDropdown}
              </>
            )}
          </div>
        </div>
        {/* One row on phones: a small fixed-width search first, then the live matches, which take
            the rest of the row and scroll sideways. With nothing live the ticker renders nothing,
            so search is the last child and `last:` lets it fill the row. */}
        <div className="flex items-center gap-2 px-3 pb-2">
          <div className="w-[38%] min-w-[110px] shrink-0 last:w-auto last:flex-1">
            <SearchDropdown compact />
          </div>
          <LiveMatchTicker className="flex-1" />
        </div>
      </div>

      {showWalletActions && <DepositModal />}
      {showWalletActions && <WithdrawModal />}
      <SetButtonValueModal />
      <RulesModal />
      <AgeRestrictionModal open={ageModalOpen} onClose={() => dispatch(setAgeModalOpen(false))} />
      {/* First login: the password must be changed before anything else. It can't be dismissed;
          it closes once /users/me reports passwordChanged (the change refetches it). */}
      <ChangePasswordModal
        open={isAuthenticated && info?.passwordChanged === false}
        onClose={() => undefined}
      />
      <ExposureSummaryModal
        open={isExposureModalOpen}
        onClose={() => setIsExposureModalOpen(false)}
      />
    </header>
  );
};

export default Header;
